import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { tourKeyFor } from '../components/DashboardTour.jsx';
import { toast } from '../components/Toasts.jsx';
import ChipInput from '../components/ChipInput.jsx';
import AiSuggestPanel from '../components/AiSuggestPanel.jsx';
import { normalizePrefs, prefsPayload } from '../util/prefs.js';

// T073 — Local-storage resume: if the operator refreshes / closes the
// tab mid-wizard, they pick up at the same step with the same draft
// state. Keyed by userId so multiple test accounts on one browser don't
// collide. Cleared at the end of finish().
const RESUME_KEY_PREFIX = 'hunter.onboarding.v1.';

// T071 — First-login onboarding wizard.
//
// Walks a brand-new operator through the steps that turn an empty
// account into a usable hunter profile: upload CV → (optional)
// LinkedIn import → hunt preferences → (optional) AI key → done.
// Each step uses the same backend endpoints the rest of the app uses;
// no new server-side surface beyond POST /api/auth/complete-onboarding.
//
// Reachable at /onboarding. RequireAuth in App.jsx redirects every
// authenticated route to /onboarding while user.onboardedAt is null,
// so the operator can't skip it by deep-linking to /dashboard.

// AI key comes BEFORE the CV/LinkedIn import steps because parsing a CV or
// LinkedIn PDF goes through Claude — without a key, those uploads fail at
// the API edge. Operators can still skip the key (the scanner + scorer
// work without it), but the CV step is now also skippable when no key is
// set, with copy that tells them to fill the profile manually from the
// Profile page later.
// T086 — Profile Interview step. After CV upload, Claude generates 3-5
// follow-up questions; the operator answers them; we merge the answers
// back into the profile. Only renders when an AI key is set + a CV has
// been parsed (otherwise: empty profile, nothing to interview about).
const STEPS = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'ai-key', label: 'AI key' },
  { id: 'cv', label: 'CV' },
  { id: 'interview', label: 'Q&A' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'prefs', label: 'Preferences' },
  { id: 'done', label: 'Done' },
];

export default function OnboardingPage() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();

  const resumeKey = user?.id ? `${RESUME_KEY_PREFIX}${user.id}` : null;
  const initialFromStorage = (() => {
    if (!resumeKey) return null;
    try {
      const raw = localStorage.getItem(resumeKey);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  })();

  const [stepIndex, setStepIndex] = useState(initialFromStorage?.stepIndex ?? 0);
  const [state, setState] = useState({
    cvDocumentId: initialFromStorage?.state?.cvDocumentId ?? null,
    cvDraft: initialFromStorage?.state?.cvDraft ?? null, // full structured profile draft (chips, roles, etc.)
    cvDraftSummary: initialFromStorage?.state?.cvDraftSummary ?? null, // counts (kept for the green confirm card)
    // T126 — staged upload progress (transient; never persisted to localStorage).
    // 'idle' | 'uploading' | 'extracting' | 'applying'
    cvPhase: 'idle',
    linkedinImported: initialFromStorage?.state?.linkedinImported ?? false,
    linkedinPhase: 'idle',
    prefs: initialFromStorage?.state?.prefs ?? normalizePrefs(user?.preferences ?? {}),
    anthropicKey: '', // never persisted — secrets don't go in localStorage
    anthropicKeyValid: initialFromStorage?.state?.anthropicKeyValid ?? false,
    // Written to the resume payload but never read back, which made the guard
    // in the first-scan effect dead: refreshing on the Done step enqueued a
    // second full scan across every source.
    firstScanDone: initialFromStorage?.state?.firstScanDone ?? false,
    finishing: false,
    // T073 — first-scan auto-trigger state
    cvError: '', // transient — deliberately not in the resume payload
    scanStatus: 'idle', // 'idle' | 'running' | 'done' | 'failed' | 'slow'
    topRecsFailed: false,
    scanResult: null, // DailyScan summary
    scanTopRecs: [],
  });

  // Persist on every step or state change (sans transient flags).
  useEffect(() => {
    if (!resumeKey) return;
    const persistable = {
      stepIndex,
      state: {
        cvDocumentId: state.cvDocumentId,
        cvDraft: state.cvDraft,
        cvDraftSummary: state.cvDraftSummary,
        linkedinImported: state.linkedinImported,
        prefs: state.prefs,
        anthropicKeyValid: state.anthropicKeyValid,
        firstScanDone: state.firstScanDone,
      },
    };
    try {
      localStorage.setItem(resumeKey, JSON.stringify(persistable));
    } catch {
      /* quota / private mode — silently skip */
    }
  }, [
    resumeKey,
    stepIndex,
    state.cvDocumentId,
    state.cvDraft,
    state.cvDraftSummary,
    state.linkedinImported,
    state.prefs,
    state.anthropicKeyValid,
    state.firstScanDone,
  ]);

  const step = STEPS[stepIndex];
  const goNext = () => setStepIndex((i) => Math.min(STEPS.length - 1, i + 1));
  const goPrev = () => setStepIndex((i) => Math.max(0, i - 1));

  // T126 — which conditional steps are currently reachable, and why not.
  // Computed per-render (depends on live state, not the static STEPS const).
  const aiKeyReady = state.anthropicKeyValid || !!user?.connectors?.anthropicKey;
  const stepReasons = computeStepReasons({ aiKeyReady, cvParsed: !!state.cvDocumentId });

  // ─── Step actions ───────────────────────────────────────────────────────

  const uploadAndParseCv = async (file) => {
    if (!file) return;
    setState((s) => ({ ...s, cvPhase: 'uploading', cvError: '' }));
    try {
      const upBody = await api.upload('/documents', file, { type: 'cv' });
      const docId = upBody.document?.id || upBody.id;
      if (!docId) throw new Error('Upload succeeded but no document id was returned.');

      // Parse via Claude. The endpoint returns { draft, changes, source }.
      setState((s) => ({ ...s, cvPhase: 'extracting' }));
      const parsed = await api.post(`/profile/import-cv?documentId=${docId}`, {});
      const draft = parsed.draft ?? {};
      // Persist the draft as the operator's profile (snapshot=cv so the
      // CV blob is also stored for future merges).
      setState((s) => ({ ...s, cvPhase: 'applying' }));
      await api.put('/profile?snapshot=cv', draft);

      setState((s) => ({
        ...s,
        cvDocumentId: docId,
        cvPhase: 'idle',
        cvDraft: {
          // Persist a slim subset for the live preview (full draft is
          // applied to the profile already; we only need what the UI shows).
          summary: draft.summary ?? '',
          skills: (draft.skills ?? []).slice(0, 60),
          experience: (draft.experience ?? []).slice(0, 10).map((e) => ({
            company: e.company ?? '',
            title: e.title ?? '',
            start: e.start ?? '',
            end: e.end ?? '',
          })),
          education: (draft.education ?? []).slice(0, 6).map((e) => ({
            school: e.school ?? '',
            degree: e.degree ?? '',
            field: e.field ?? '',
            startYear: e.startYear ?? '',
            endYear: e.endYear ?? '',
          })),
          certifications: (draft.certifications ?? []).slice(0, 12).map((c) => c?.name ?? ''),
          languages: (draft.languages ?? []).slice(0, 8).map((l) => l?.name ?? ''),
        },
        cvDraftSummary: {
          summary: !!draft.summary,
          skills: (draft.skills ?? []).length,
          experience: (draft.experience ?? []).length,
          education: (draft.education ?? []).length,
          certifications: (draft.certifications ?? []).length,
          languages: (draft.languages ?? []).length,
        },
      }));
      toast('CV parsed + applied', { kind: 'success' });
    } catch (e) {
      // T266(4) — a transient toast was the only report of a failed parse, on
      // the one step that used to refuse to advance without one. The operator
      // saw the toast fade and was left facing a drop zone that looked idle,
      // with no way forward and nothing on screen saying why. Same persistent
      // inline treatment the key step already uses.
      const msg = e?.body?.message || e?.message || 'CV import failed';
      setState((s) => ({ ...s, cvPhase: 'idle', cvError: msg }));
    }
  };

  const uploadAndImportLinkedin = async (file) => {
    if (!file) return;
    setState((s) => ({ ...s, linkedinPhase: 'uploading' }));
    try {
      const isZip = /\.zip$/i.test(file.name) || file.type.includes('zip');
      const docType = isZip ? 'linkedin-export' : 'linkedin-pdf';
      const upBody = await api.upload('/documents', file, { type: docType });
      const docId = upBody.document?.id || upBody.id;

      // ZIP parses locally (no Claude); PDF goes through Claude. Either way
      // this is the "parsing" boundary.
      setState((s) => ({ ...s, linkedinPhase: 'extracting' }));
      const parsed = isZip
        ? await api.post(`/profile/import-linkedin-zip?documentId=${docId}`, {})
        : await api.post(`/profile/import-cv?documentId=${docId}`, {});
      const draft = parsed.draft ?? {};
      // Merge into the existing profile by applying the LinkedIn draft as
      // a snapshot=linkedin so the CV blob stays the operator's main
      // record but LinkedIn data fills the gaps.
      setState((s) => ({ ...s, linkedinPhase: 'applying' }));
      await api.put('/profile?snapshot=linkedin', draft);
      setState((s) => ({ ...s, linkedinPhase: 'idle', linkedinImported: true }));
      toast('LinkedIn data imported', { kind: 'success' });
    } catch (e) {
      setState((s) => ({ ...s, linkedinPhase: 'idle' }));
      toast(e?.body?.message || e?.message || 'LinkedIn import failed', { kind: 'error' });
    }
  };

  const savePreferences = async () => {
    // T280 — the gate below this was written, commented, and never called. The
    // wizard advanced on a successful PATCH whatever the operator had entered,
    // so onboarding could finish with no target roles and no target locations —
    // which is precisely the state T242 has to explain afterwards on the
    // dashboard ("there was nothing to score jobs against"). Refusing here is
    // both cheaper and truthful about why.
    if (!canLeaveStep()) {
      toast('Add at least one target role and one target location first', { kind: 'error' });
      return;
    }
    try {
      // T271(3) — the wizard used to build its own body, which is how it came
      // to omit excludedCompanies and digestHour and to treat a salary of 0 as
      // "unset" (`? :` rather than a null check).
      await api.patch('/preferences', prefsPayload(state.prefs));
      toast('Preferences saved', { kind: 'success' });
      goNext();
    } catch (e) {
      toast(e?.body?.message || e?.message || 'Save failed', { kind: 'error' });
    }
  };

  const [keyValidating, setKeyValidating] = useState(false);
  const [keyError, setKeyError] = useState('');

  const saveAnthropicKey = async () => {
    const trimmed = state.anthropicKey.trim();
    if (!trimmed) {
      goNext();
      return;
    }
    // T073 — Validate BEFORE saving. countTokens is a 0-cost auth probe
    // (Anthropic doesn't bill it). Catches typos + revoked keys before
    // they break a downstream cover letter or interview prep call.
    setKeyValidating(true);
    setKeyError('');
    try {
      await api.post('/connectors/validate-anthropic', { anthropicKey: trimmed });
      // Valid — save it.
      await api.patch('/connectors', { anthropicKey: trimmed });
      setState((s) => ({ ...s, anthropicKeyValid: true }));
      toast('Anthropic key validated + saved', { kind: 'success' });
      goNext();
    } catch (e) {
      const msg = e?.body?.message || e?.message || 'Key validation failed';
      setKeyError(msg);
      // No toast — the inline message is louder + clearer.
    } finally {
      setKeyValidating(false);
    }
  };

  // T073 — auto-trigger the first scan when the wizard reaches the Done step,
  // so the operator watches it work instead of landing on an empty dashboard.
  //
  // T266(2) — it used to lie. POST /scans/trigger answers 202 with a scanId the
  // moment the job is ENQUEUED; the old code treated that response as the
  // finished scan, so the panel announced "First scan done — 0 matches" while
  // the scan was still fanning out across sixty-odd sources, and the operator
  // finished onboarding believing his setup had found nothing. Poll to a
  // terminal status, like the dashboard already does.
  //
  // `firstScanDone` also lives in the resume payload now: the comment claiming
  // a refresh on Done would not re-trigger was false — `firstScanStarted` is a
  // ref, so a reload started a second scan.
  const firstScanStarted = useRef(false);
  const scanPollRef = useRef(null);
  useEffect(() => () => clearTimeout(scanPollRef.current), []);

  useEffect(() => {
    if (step.id !== 'done' || firstScanStarted.current || state.firstScanDone) return;
    firstScanStarted.current = true;
    setState((s) => ({ ...s, scanStatus: 'running' }));

    const settle = async (scan) => {
      let top = [];
      try {
        const r = await api.get('/recommendations?limit=3&minScore=20');
        top = r.recommendations ?? [];
      } catch {
        // T269(6) — an empty catch here used to leave the panel claiming zero
        // matches when the fetch, not the scan, was what failed.
        top = null;
      }
      setState((s) => ({
        ...s,
        scanStatus: scan.status === 'failed' ? 'failed' : 'done',
        scanResult: scan,
        scanTopRecs: top ?? [],
        topRecsFailed: top === null,
        firstScanDone: true,
      }));
    };

    const MAX_MS = 5 * 60 * 1000;
    const startedAt = Date.now();
    const poll = async (scanId) => {
      try {
        const scan = await api.get(`/scans/${scanId}`);
        if (scan.status === 'completed' || scan.status === 'failed') return settle(scan);
        if (Date.now() - startedAt > MAX_MS) {
          // The job keeps running server-side; say so rather than claim a result.
          setState((s) => ({ ...s, scanStatus: 'slow', firstScanDone: true }));
          return undefined;
        }
        setState((s) => ({ ...s, scanResult: scan }));
      } catch {
        if (Date.now() - startedAt > MAX_MS) {
          setState((s) => ({ ...s, scanStatus: 'slow', firstScanDone: true }));
          return undefined;
        }
      }
      scanPollRef.current = setTimeout(() => poll(scanId), 2500);
      return undefined;
    };

    api
      .post('/scans/trigger', {})
      .then((r) => {
        if (!r?.scanId) return settle({ status: 'completed' });
        scanPollRef.current = setTimeout(() => poll(r.scanId), 1500);
        return undefined;
      })
      .catch((e) => {
        setState((s) => ({
          ...s,
          scanStatus: 'failed',
          scanResult: { error: e?.body?.message || e?.message },
          firstScanDone: true,
        }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.id]);

  const finish = async () => {
    setState((s) => ({ ...s, finishing: true }));
    try {
      await api.post('/auth/complete-onboarding', {});
      await refresh?.();
      // T073 — clear the resume payload now that onboarding is done.
      if (resumeKey) {
        try { localStorage.removeItem(resumeKey); } catch { /* ignore */ }
      }
      // T139 — pre-mark the dashboard tour as seen: a user who just finished the
      // wizard must not get the first-run tour stacked on top of it.
      // T269(1) — was a duplicated literal; if DashboardTour ever bumps its key
      // version, a wizard graduate gets the first-run tour stacked on top.
      // T290b — per-account, like the tour's own read. Pre-setting the global
      // key here would have marked the tour "seen" for every other account on
      // this browser as a side effect of one person finishing onboarding.
      try { localStorage.setItem(tourKeyFor(user), '1'); } catch { /* ignore */ }
      navigate('/');
    } catch (e) {
      setState((s) => ({ ...s, finishing: false }));
      toast(e?.body?.message || e?.message || 'Finish failed', { kind: 'error' });
    }
  };

  // ─── Validation gates ───────────────────────────────────────────────────
  const canLeaveStep = () => {
    // T266(1) — the CV step no longer gates. It used to require a parsed CV
    // whenever an AI key was present, which paired with the hidden skip button
    // to make an unparseable CV a hard lock on the whole app. A CV improves
    // scoring; it is not what the app needs to run.

    if (step.id === 'prefs') {
      return state.prefs.targetRoles.length > 0 && state.prefs.targetLocations.length > 0;
    }
    return true;
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-2xl px-4 py-12">
        <header className="mb-8">
          {/* T269(5) — the wizard had no exit: no sign-out, and on the last
              step no way back either. A re-run from Settings therefore had a
              one-way door, which is the same trap as the CV dead end. */}
          <div className="mb-4 flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-widest text-brand-500">First-time setup</p>
            <button
              type="button"
              onClick={logout}
              className="text-xs text-slate-500 underline decoration-dotted hover:text-slate-300"
            >
              Sign out
            </button>
          </div>
          <h1 className="mt-1 text-2xl font-semibold">Let's get hunter pointed at the right jobs</h1>
          <p className="mt-2 text-sm text-slate-400">
            Takes ~10 minutes. Start with an Anthropic API key if you have one — CV parsing, cover letters and interview prep all need it. Then upload your CV (so we know what you've done) and set what you're hunting for (so the scanner only surfaces what matters). Every step can be skipped and redone later.
          </p>
        </header>

        <Stepper steps={STEPS} currentIndex={stepIndex} reasons={stepReasons} />
        {Object.entries(stepReasons).some(([, r]) => r) && (
          <p className="mt-1.5 text-xs text-slate-500" aria-live="polite">
            {Object.entries(stepReasons)
              .filter(([, r]) => r)
              .map(([id, r]) => `${STEPS.find((s) => s.id === id)?.label ?? id} — ${r} (skippable)`)
              .join(' · ')}
          </p>
        )}

        <main className="mt-8 rounded-xl border border-slate-800 bg-slate-900/40 p-6">
          {step.id === 'welcome' && <StepWelcome onNext={goNext} />}

          {step.id === 'cv' && (
            <StepCv
              phase={state.cvPhase}
              summary={state.cvDraftSummary}
              draft={state.cvDraft}
              uploadedId={state.cvDocumentId}
              aiKeyReady={state.anthropicKeyValid || !!user?.connectors?.anthropicKey}
              error={state.cvError}
              onUpload={uploadAndParseCv}
              onSkip={goNext}
              onNext={goNext}
            />
          )}

          {step.id === 'interview' && (
            <StepInterview
              cvParsed={!!state.cvDocumentId}
              aiKeyReady={state.anthropicKeyValid || !!user?.connectors?.anthropicKey}
              onSkip={goNext}
              onComplete={goNext}
              refreshUser={refresh}
            />
          )}

          {step.id === 'linkedin' && (
            <StepLinkedin
              phase={state.linkedinPhase}
              imported={state.linkedinImported}
              aiKeyReady={state.anthropicKeyValid || !!user?.connectors?.anthropicKey}
              onUpload={uploadAndImportLinkedin}
              onSkip={goNext}
              onNext={goNext}
            />
          )}

          {step.id === 'prefs' && (
            <StepPrefs
              prefs={state.prefs}
              setPrefs={(updater) =>
                setState((s) => ({ ...s, prefs: typeof updater === 'function' ? updater(s.prefs) : updater }))
              }
              aiKeyReady={state.anthropicKeyValid || !!user?.connectors?.anthropicKey}
              onSave={savePreferences}
            />
          )}

          {step.id === 'ai-key' && (
            <StepAiKey
              value={state.anthropicKey}
              onChange={(v) => {
                setKeyError('');
                setState((s) => ({ ...s, anthropicKey: v }));
              }}
              onSave={saveAnthropicKey}
              onSkip={goNext}
              validating={keyValidating}
              error={keyError}
            />
          )}

          {step.id === 'done' && (
            <StepDone
              summary={state.cvDraftSummary}
              linkedinImported={state.linkedinImported}
              aiKeySet={state.anthropicKeyValid || !!user?.connectors?.anthropicKey}
              prefs={state.prefs}
              scanStatus={state.scanStatus}
              scanResult={state.scanResult}
              scanTopRecs={state.scanTopRecs}
              topRecsFailed={state.topRecsFailed}
              alreadyRan={state.firstScanDone}
              onFinish={finish}
              finishing={state.finishing}
            />
          )}
        </main>

        {/* Nav row. T269(3) — the second "Next →" that used to sit here on the
            CV step was a duplicate of the "Looks right — next →" inside the
            parsed-CV preview, two identical actions a few pixels apart.
            T269(5) — Back now survives onto Done, which had no way back at all. */}
        {stepIndex > 0 && (
          <div className="mt-6 flex items-center justify-between">
            <button
              type="button"
              onClick={goPrev}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-400 hover:border-slate-500 hover:text-slate-200"
            >
              ← Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Stepper ─────────────────────────────────────────────────────────────
function Stepper({ steps, currentIndex, reasons }) {
  return (
    <ol
      aria-label={`Step ${currentIndex + 1} of ${steps.length}`}
      className="flex items-center gap-2 overflow-x-auto text-xs"
    >
      {steps.map((s, i) => {
        const done = i < currentIndex;
        const current = i === currentIndex;
        // T126 — a future step that isn't reachable yet is shown greyed +
        // dashed with the reason as a tooltip (the reason is also printed
        // below the strip, so it's not tooltip-only).
        const reason = !current && !done ? reasons?.[s.id] : null;
        return (
          <li
            key={s.id}
            aria-current={current ? 'step' : undefined}
            title={reason || undefined}
            className="flex items-center gap-2"
          >
            <span
              className={
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[10px] font-medium ' +
                (current
                  ? 'border-brand-500 bg-brand-600 text-white'
                  : done
                    ? 'border-emerald-700 bg-emerald-950/40 text-emerald-300'
                    : reason
                      ? 'border-dashed border-slate-700 text-slate-600'
                      : 'border-slate-700 text-slate-500')
              }
            >
              {done ? '✓' : i + 1}
            </span>
            <span
              className={
                (current ? 'text-slate-100' : done ? 'text-slate-400' : 'text-slate-600') +
                (reason ? ' italic' : '')
              }
            >
              {s.label}
            </span>
            {i < steps.length - 1 && <span className="text-slate-700">·</span>}
          </li>
        );
      })}
    </ol>
  );
}

// ─── T126 — staged upload progress + cost badges + step reachability ───────
const CV_STAGES = [
  { id: 'uploading', label: 'Uploading file' },
  { id: 'extracting', label: 'Extracting with Claude' },
  { id: 'applying', label: 'Applying to profile' },
];
const LINKEDIN_STAGES = [
  { id: 'uploading', label: 'Uploading file' },
  { id: 'extracting', label: 'Parsing export' },
  { id: 'applying', label: 'Applying to profile' },
];

// Pure: which conditional steps are reachable and, if not, why. Computed from
// live state per-render (not baked into the STEPS const). Exported for tests.
export function computeStepReasons({ aiKeyReady, cvParsed }) {
  return {
    interview: !aiKeyReady
      ? 'Needs an Anthropic key'
      : !cvParsed
        ? 'Needs a parsed CV first'
        : null,
  };
}

// Three-stage progress for CV / LinkedIn uploads — current pulses, completed
// are checked. aria-live so screen readers announce each phase. Exported for tests.
export function StagedProgress({ phase, steps }) {
  if (phase === 'idle') return null;
  const curIdx = steps.findIndex((s) => s.id === phase);
  return (
    <ol
      aria-live="polite"
      className="mt-2 space-y-1.5 rounded-lg border border-brand-700 bg-brand-600/10 p-3 text-xs"
    >
      {steps.map((s, i) => {
        const done = i < curIdx;
        const current = i === curIdx;
        return (
          <li
            key={s.id}
            className={
              'flex items-center gap-2 ' +
              (current ? 'text-brand-300' : done ? 'text-emerald-300' : 'text-slate-500')
            }
          >
            <span
              className={
                'inline-block h-2 w-2 rounded-full ' +
                (current ? 'animate-pulse bg-brand-500' : done ? 'bg-emerald-500' : 'bg-slate-700')
              }
            />
            <span>
              {done ? '✓ ' : ''}
              {s.label}
              {current ? '…' : ''}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

// Free (green) vs paid (amber) cost/time pill for the upload drop zones.
// Exported for tests.
export function CostBadge({ tone, children }) {
  const cls =
    tone === 'free'
      ? 'border-emerald-700 bg-emerald-950/20 text-emerald-300'
      : 'border-amber-700 bg-amber-950/15 text-amber-300';
  return <span className={'rounded-full border px-2 py-0.5 text-[10px] ' + cls}>{children}</span>;
}

// ─── Step 1: Welcome ─────────────────────────────────────────────────────
function StepWelcome({ onNext }) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-slate-100">A quick map of what's ahead</h2>
      <ol className="ml-5 list-decimal space-y-2 text-sm text-slate-300">
        <li><strong>Anthropic API key</strong> — optional but recommended. Unlocks Claude-powered CV/LinkedIn parsing on the next steps plus cover letters, interview prep, tailored CV summaries. Pennies per use, paid directly to Anthropic. <em>Without a key you can still use hunter — but CV/LinkedIn uploads are disabled and you'll fill the profile by hand.</em></li>
        <li><strong>Upload your CV</strong> — PDF, .doc, or .docx. Claude extracts skills + experience so the scorer has something to match against.</li>
        <li><strong>Profile Q&A</strong> — Claude asks 3-5 follow-ups to fill gaps the CV left behind (scope, scale, tooling specifics). Answers fold into your profile.</li>
        <li><strong>(Optional) LinkedIn import</strong> — drop the data export ZIP (parses locally, no key needed) or your LinkedIn PDF (parses via Claude).</li>
        <li><strong>Hunt preferences</strong> — target roles, locations, seniority. Claude can suggest defaults based on your profile. The daily scan only surfaces jobs that match these.</li>
      </ol>
      <p className="text-xs text-slate-500">
        You can change any of this later from the Profile + Settings pages.
      </p>
      <div className="pt-2">
        <button
          type="button"
          onClick={onNext}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-500"
        >
          Let's start →
        </button>
      </div>
    </div>
  );
}

// ─── Step 2: CV upload ───────────────────────────────────────────────────
export function StepCv({ phase, summary, draft, uploadedId, aiKeyReady, error, onUpload, onSkip, onNext }) {
  const parsing = phase !== 'idle';
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-100">Upload your CV</h2>
        <p className="mt-1 text-sm text-slate-400">
          PDF, .doc, or .docx · max 25MB. We send the file to Claude for structured extraction.
        </p>
        <div className="mt-2">
          <CostBadge tone="paid">PDF/DOC · ~$0.01–0.03 · ~30s · uses Claude</CostBadge>
        </div>
      </div>
      {!aiKeyReady && (
        <div className="rounded-lg border border-amber-700 bg-amber-950/20 p-3 text-xs text-amber-200">
          <p className="font-medium text-amber-300">No Anthropic key set — CV parsing is unavailable.</p>
          <p className="mt-1 text-amber-200/80">
            CV extraction uses Claude. You can skip this step now and either add a key from <strong>Settings → Connectors</strong> later, or fill in your profile by hand from the <strong>Profile</strong> page. Skipping won't block onboarding.
          </p>
        </div>
      )}
      <FileDrop
        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        disabled={parsing || !aiKeyReady}
        label={
          !aiKeyReady
            ? 'upload disabled — no Anthropic key'
            : phase === 'uploading'
              ? 'Uploading…'
              : phase === 'extracting'
                ? 'extracting with Claude…'
                : phase === 'applying'
                  ? 'applying to your profile…'
                  : uploadedId
                    ? 'CV parsed ✓ — drop another to redo'
                    : 'drop your CV here, or click to choose'
        }
        onFile={onUpload}
      />
      {parsing && <StagedProgress phase={phase} steps={CV_STAGES} />}
      {error && !parsing && (
        <div role="alert" className="rounded-lg border border-red-800 bg-red-950/20 p-3 text-xs">
          <p className="font-medium text-red-300">Could not read that CV</p>
          <p className="mt-1 text-red-200">{error}</p>
          <p className="mt-1 text-red-300">
            Try a different export of the file, or skip this step — you can upload it later from Profile.
          </p>
        </div>
      )}
      {/* T266(1) — this escape hatch used to be hidden whenever an AI key was
          configured, while canLeaveStep simultaneously required a parsed CV to
          advance. With a key set and a CV that will not parse, there was no way
          forward and no way out — and RequireAuth sends every route back into
          the wizard, so a persistent parse failure or a re-run from Settings
          locked the operator out of the entire application. Always skippable. */}
      <div>
        <button
          type="button"
          onClick={onSkip}
          className="rounded-md border border-slate-700 px-4 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
        >
          Skip CV upload →
        </button>
        <p className="mt-1 text-[11px] text-slate-500">
          You can upload it later from Profile — scoring just works from your preferences until you do.
        </p>
      </div>
      {summary && draft && (
        <div className="space-y-3 rounded-lg border border-emerald-800 bg-emerald-950/20 p-4">
          <div>
            <p className="font-medium text-emerald-300">CV extracted ✓</p>
            <p className="mt-1 text-xs text-emerald-200">
              <strong>{summary.skills}</strong> skills ·{' '}
              <strong>{summary.experience}</strong> roles ·{' '}
              <strong>{summary.education}</strong> education ·{' '}
              <strong>{summary.certifications}</strong> certs ·{' '}
              <strong>{summary.languages}</strong> languages
            </p>
          </div>

          {/* Skills preview */}
          {draft.skills?.length > 0 && (
            <PreviewBlock label="Skills found">
              <div className="flex flex-wrap gap-1">
                {draft.skills.slice(0, 30).map((s) => (
                  <span key={s} className="rounded-full border border-emerald-700/60 bg-emerald-950/30 px-2 py-0.5 text-[10px] text-emerald-200">{s}</span>
                ))}
                {draft.skills.length > 30 && (
                  <span className="px-1 text-[10px] text-emerald-400/60">+{draft.skills.length - 30} more</span>
                )}
              </div>
            </PreviewBlock>
          )}

          {/* Experience preview */}
          {draft.experience?.length > 0 && (
            <PreviewBlock label="Recent roles">
              <ul className="space-y-1 text-xs text-emerald-100">
                {draft.experience.slice(0, 5).map((e, i) => (
                  <li key={i} className="flex items-baseline justify-between gap-2">
                    <span className="min-w-0 truncate">
                      <strong className="text-emerald-200">{e.title || '?'}</strong>
                      <span className="text-emerald-300/70"> @ {e.company || '?'}</span>
                    </span>
                    <span className="shrink-0 font-mono text-[10px] text-emerald-400/70">
                      {e.start || '?'} → {e.end || 'present'}
                    </span>
                  </li>
                ))}
                {draft.experience.length > 5 && (
                  <li className="text-[10px] text-emerald-400/60">+{draft.experience.length - 5} more roles</li>
                )}
              </ul>
            </PreviewBlock>
          )}

          {/* Education preview */}
          {draft.education?.length > 0 && (
            <PreviewBlock label="Education">
              <ul className="space-y-1 text-xs text-emerald-100">
                {draft.education.slice(0, 4).map((e, i) => (
                  <li key={i} className="truncate">
                    <strong className="text-emerald-200">{[e.degree, e.field].filter(Boolean).join(' ') || 'Studies'}</strong>
                    <span className="text-emerald-300/70"> @ {e.school || '?'}</span>
                    {(e.startYear || e.endYear) && (
                      <span className="ml-2 font-mono text-[10px] text-emerald-400/70">
                        {e.startYear || '?'}–{e.endYear || 'present'}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </PreviewBlock>
          )}

          {/* Certifications + languages — compact one-liner each */}
          {(draft.certifications?.length > 0 || draft.languages?.length > 0) && (
            <div className="space-y-1 text-xs text-emerald-200">
              {draft.certifications?.length > 0 && (
                <p className="truncate"><span className="text-emerald-400">Certifications · </span>{draft.certifications.slice(0, 6).join(' · ')}{draft.certifications.length > 6 ? ` +${draft.certifications.length - 6}` : ''}</p>
              )}
              {draft.languages?.length > 0 && (
                <p className="truncate"><span className="text-emerald-400">Languages · </span>{draft.languages.join(' · ')}</p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              onClick={onNext}
              className="rounded-md bg-brand-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-brand-500"
            >
              Looks right — next →
            </button>
            <span className="text-[10px] text-emerald-400">
              If anything's wrong, drop a different CV above and we'll re-parse.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewBlock({ label, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-emerald-400/80">{label}</p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ─── Step 3: LinkedIn import (optional) ──────────────────────────────────
// T086 — Profile Interview step. Generates 3-5 follow-up questions via
// Claude, collects answers, merges them back into the profile.
function StepInterview({ cvParsed, aiKeyReady, onSkip, onComplete, refreshUser }) {
  const [phase, setPhase] = useState('idle'); // 'idle' | 'loading' | 'asking' | 'merging' | 'done' | 'error'
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [mergeResult, setMergeResult] = useState(null);
  const [error, setError] = useState('');

  // No CV + no AI key → step has nothing useful to do. Operator can skip.
  if (!cvParsed || !aiKeyReady) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-slate-100">Profile interview <span className="ml-2 text-xs text-slate-500">(optional)</span></h2>
        <p className="text-sm text-slate-400">
          {!aiKeyReady
            ? 'No Anthropic key — the AI interview needs Claude. Skip for now, set a key from Settings later if you want this step.'
            : 'Skipped CV upload — there\'s no profile to enrich yet. You can run the interview later from Profile.'}
        </p>
        <button
          type="button"
          onClick={onSkip}
          className="rounded-md border border-slate-700 px-4 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
        >
          Skip →
        </button>
      </div>
    );
  }

  const startInterview = async () => {
    setPhase('loading');
    setError('');
    try {
      const r = await api.post('/profile/ai/interview', {});
      setQuestions(r.questions ?? []);
      setAnswers(Object.fromEntries((r.questions ?? []).map((_, i) => [i, ''])));
      setPhase('asking');
    } catch (e) {
      setError(e?.body?.message || e?.message || 'Could not load interview');
      setPhase('error');
    }
  };

  const submitAnswers = async () => {
    // Only send Q/A pairs where the operator actually typed something.
    const qa = questions
      .map((q, i) => ({ question: q.question, field: q.field, answer: (answers[i] ?? '').trim() }))
      .filter((x) => x.answer);
    if (qa.length === 0) {
      toast('Add at least one answer first (or skip)', { kind: 'info' });
      return;
    }
    setPhase('merging');
    setError('');
    try {
      const r = await api.post('/profile/ai/merge-interview', { qa });
      setMergeResult(r.merged);
      await refreshUser?.();
      setPhase('done');
      toast('Profile enriched ✓', { kind: 'success' });
    } catch (e) {
      setError(e?.body?.message || e?.message || 'Merge failed');
      setPhase('error');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-100">Profile interview <span className="ml-2 text-xs text-slate-500">(optional)</span></h2>
        <p className="mt-1 text-sm text-slate-400">
          CVs leave gaps — scale, scope, tooling specifics that didn't fit. Claude reads your parsed profile and asks 3-5 follow-up questions so the job scorer has more signal to match against. Answers are merged back into your profile. ~30 seconds.
        </p>
      </div>

      {phase === 'idle' && (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={startInterview}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-500"
          >
            Start interview →
          </button>
          <button
            type="button"
            onClick={onSkip}
            className="rounded-md border border-slate-700 px-4 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
          >
            Skip
          </button>
        </div>
      )}

      {phase === 'loading' && (
        <p className="text-sm text-brand-300">
          <span className="mr-2 inline-block h-2 w-2 animate-pulse rounded-full bg-brand-500" />
          Claude is reading your profile + picking questions…
        </p>
      )}

      {phase === 'asking' && (
        <div className="space-y-4">
          {questions.map((q, i) => (
            <div key={i} className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
              <label className="text-sm font-medium text-slate-100">{q.question}</label>
              {q.why && <p className="mt-0.5 text-[11px] italic text-slate-500">{q.why}</p>}
              <textarea
                value={answers[i] ?? ''}
                onChange={(e) => setAnswers((cur) => ({ ...cur, [i]: e.target.value }))}
                rows={3}
                placeholder="Your answer — 1-3 sentences."
                className="mt-2 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
              />
            </div>
          ))}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={submitAnswers}
              className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-500"
            >
              Save answers + enrich →
            </button>
            <button
              type="button"
              onClick={onSkip}
              className="rounded-md border border-slate-700 px-4 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
            >
              Skip — answer later from Profile
            </button>
          </div>
        </div>
      )}

      {phase === 'merging' && (
        <p className="text-sm text-brand-300">
          <span className="mr-2 inline-block h-2 w-2 animate-pulse rounded-full bg-brand-500" />
          Merging your answers into the profile…
        </p>
      )}

      {phase === 'done' && (
        <div className="space-y-3 rounded-lg border border-emerald-800 bg-emerald-950/20 p-4">
          <p className="font-medium text-emerald-300">Profile enriched ✓</p>
          {mergeResult?.notes && <p className="text-sm text-emerald-200">{mergeResult.notes}</p>}
          <ul className="space-y-1 text-xs text-emerald-100">
            {mergeResult?.addedSkills?.length > 0 && (
              <li><strong>Skills added:</strong> {mergeResult.addedSkills.join(', ')}</li>
            )}
            {mergeResult?.addedHighlights?.length > 0 && (
              <li>
                <strong>Highlights added:</strong>
                <ul className="ml-4 mt-0.5 list-disc space-y-0.5">
                  {mergeResult.addedHighlights.map((h, i) => (
                    <li key={i}>{h.company}: <em>{h.highlight}</em></li>
                  ))}
                </ul>
              </li>
            )}
            {mergeResult?.suggestedSeniority && (
              <li><strong>Seniority updated:</strong> {mergeResult.suggestedSeniority}</li>
            )}
          </ul>
          <button
            type="button"
            onClick={onComplete}
            className="rounded-md bg-brand-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-brand-500"
          >
            Continue →
          </button>
        </div>
      )}

      {phase === 'error' && (
        <div className="space-y-2 rounded-lg border border-red-800 bg-red-950/20 p-3">
          <p className="text-sm text-red-300">⚠ {error}</p>
          <div className="flex gap-2">
            <button type="button" onClick={startInterview} className="rounded-md border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400">Retry</button>
            <button type="button" onClick={onSkip} className="rounded-md border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400">Skip</button>
          </div>
        </div>
      )}
    </div>
  );
}

function StepLinkedin({ phase, imported, aiKeyReady, onUpload, onSkip, onNext }) {
  // ZIP imports parse locally with no Claude call; PDF imports go through
  // Claude. When no AI key is set the ZIP route still works — narrow the
  // accept attribute accordingly and warn that PDF is disabled.
  const parsing = phase !== 'idle';
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-100">LinkedIn import <span className="ml-2 text-xs text-slate-500">(optional)</span></h2>
        <p className="mt-1 text-sm text-slate-400">
          Two routes: a <strong>LinkedIn data export ZIP</strong> (Settings → Data Privacy → Get a copy of your data; ~24h to receive) parses locally with no AI cost, or a <strong>LinkedIn PDF</strong> (Profile → More → Save to PDF) parses via Claude. Either fills gaps the CV missed.
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <CostBadge tone="free">ZIP · free · instant · parses locally</CostBadge>
          {aiKeyReady && <CostBadge tone="paid">PDF · ~$0.01 · ~10s · uses Claude</CostBadge>}
        </div>
      </div>
      {!aiKeyReady && (
        <div className="rounded-lg border border-amber-700 bg-amber-950/20 p-3 text-xs text-amber-200">
          <p>
            No Anthropic key — <strong>PDF import is disabled</strong>. The <strong>ZIP export</strong> still works (it parses locally). Or skip entirely.
          </p>
        </div>
      )}
      <FileDrop
        accept={aiKeyReady ? '.zip,.pdf,application/zip,application/pdf' : '.zip,application/zip'}
        disabled={parsing}
        label={
          phase === 'uploading'
            ? 'Uploading…'
            : phase === 'extracting'
              ? 'parsing export…'
              : phase === 'applying'
                ? 'applying to your profile…'
                : imported
                  ? 'LinkedIn data merged ✓ — drop another file to re-merge'
                  : aiKeyReady
                    ? 'drop your LinkedIn ZIP or PDF here, or click to choose'
                    : 'drop your LinkedIn ZIP here (PDF needs an AI key)'
        }
        onFile={onUpload}
      />
      {parsing && <StagedProgress phase={phase} steps={LINKEDIN_STAGES} />}
      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={onSkip}
          className="rounded-md border border-slate-700 px-4 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
        >
          Skip for now
        </button>
        {imported && (
          <button
            type="button"
            onClick={onNext}
            className="rounded-md bg-brand-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-brand-500"
          >
            Next →
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Step 4: Preferences ─────────────────────────────────────────────────
function StepPrefs({ prefs, setPrefs, aiKeyReady, onSave }) {
  const isValid = prefs.targetRoles.length > 0 && prefs.targetLocations.length > 0;
  const addRole = (title) =>
    setPrefs((p) => (p.targetRoles.includes(title) ? p : { ...p, targetRoles: [...p.targetRoles, title] }));
  const addLocation = (name) =>
    setPrefs((p) => (p.targetLocations.includes(name) ? p : { ...p, targetLocations: [...p.targetLocations, name] }));
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-100">What are you hunting for?</h2>
        <p className="mt-1 text-sm text-slate-400">
          These drive the daily scanner and the rules scorer. Vague targets = noisy feed; specific targets = sharper signal. You can edit any of this later.
          {aiKeyReady && ' Claude can suggest defaults based on your CV — use the ✨ buttons below.'}
        </p>
      </div>

      <Field label="Target roles" hint='Enter each as a phrase: "Cloud Engineer", "DevOps", "Solution Architect". Adapter substring-matches against job titles.'>
        <ChipInput
          value={prefs.targetRoles}
          onChange={(next) => setPrefs((p) => ({ ...p, targetRoles: next }))}
          placeholder="Cloud Engineer, DevOps, Solution Architect"
        />
        {aiKeyReady && (
          <AiSuggestPanel
            label="Suggest target roles"
            endpoint="/profile/ai/suggest-roles"
            itemKey="roles"
            nameKey="title"
            existing={prefs.targetRoles}
            onAdd={addRole}
            helperText="based on your CV"
          />
        )}
      </Field>

      <Field label="Target locations" hint='Cities or regions. "Copenhagen", "Remote Europe", "Berlin" — substring against job locations.'>
        <ChipInput
          value={prefs.targetLocations}
          onChange={(next) => setPrefs((p) => ({ ...p, targetLocations: next }))}
          placeholder="Copenhagen, Remote Europe"
        />
        {aiKeyReady && (
          <AiSuggestPanel
            label="Suggest target locations"
            endpoint="/profile/ai/suggest-locations"
            body={{ remotePreference: prefs.remotePreference }}
            itemKey="locations"
            nameKey="name"
            existing={prefs.targetLocations}
            onAdd={addLocation}
            helperText="DK + Nordic + EU only"
          />
        )}
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Seniority" hint="Pick one or more. Empty = any.">
          {/* T096 — multi-toggle chips */}
          <div className="flex flex-wrap gap-1">
            {['junior', 'mid', 'senior', 'staff', 'principal'].map((s) => {
              const cur = Array.isArray(prefs.seniority) ? prefs.seniority : prefs.seniority ? [prefs.seniority] : [];
              const active = cur.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() =>
                    setPrefs((p) => {
                      const arr = Array.isArray(p.seniority) ? p.seniority : p.seniority ? [p.seniority] : [];
                      const next = arr.includes(s) ? arr.filter((x) => x !== s) : [...arr, s];
                      return { ...p, seniority: next };
                    })
                  }
                  className={
                    'rounded-full border px-2.5 py-0.5 text-xs transition ' +
                    (active
                      ? 'border-brand-500 bg-brand-600/20 text-brand-200'
                      : 'border-slate-700 text-slate-400 hover:border-brand-600 hover:text-brand-400')
                  }
                >
                  {active ? '✓ ' : ''}{s}
                </button>
              );
            })}
          </div>
        </Field>
        <Field label="Remote preference" hint="">
          <select
            value={prefs.remotePreference}
            onChange={(e) => setPrefs((p) => ({ ...p, remotePreference: e.target.value }))}
            className="w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-1.5 text-sm text-slate-100"
          >
            <option value="any">any</option>
            <option value="remote">remote only</option>
            <option value="hybrid">hybrid OK</option>
            <option value="onsite">onsite only</option>
          </select>
        </Field>
        {/* T314 — the hint used to say "Currency-agnostic", and the scorer took
            it at its word: it compared this number against salaries quoted in
            any currency. Saying which currency the floor is in is what makes
            the comparison mean anything. */}
        <Field label="Salary floor (annual)" hint="Optional. Unset currency = salary is not scored.">
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              value={prefs.salaryMinimum}
              onChange={(e) => setPrefs((p) => ({ ...p, salaryMinimum: e.target.value }))}
              placeholder="e.g. 750000"
              className="flex-1 rounded-md border border-slate-700 bg-slate-950/40 px-3 py-1.5 text-sm text-slate-100"
            />
            <select
              value={prefs.salaryCurrency}
              onChange={(e) => setPrefs((p) => ({ ...p, salaryCurrency: e.target.value }))}
              aria-label="Currency of the salary floor"
              className="w-24 rounded-md border border-slate-700 bg-slate-950/40 px-2 py-1.5 text-sm text-slate-100"
            >
              <option value="">—</option>
              {['DKK', 'EUR', 'SEK', 'NOK', 'GBP', 'USD', 'CHF', 'PLN'].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </Field>
      </div>

      <div className="pt-2">
        <button
          type="button"
          onClick={onSave}
          disabled={!isValid}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-500 disabled:opacity-50"
          title={isValid ? '' : 'Need at least one target role + one target location.'}
        >
          Save preferences →
        </button>
        {!isValid && (
          <p className="mt-2 text-[11px] text-amber-400">Add at least one role and one location to continue.</p>
        )}
      </div>
    </div>
  );
}

// ─── Step 5: AI key (optional) ───────────────────────────────────────────
function StepAiKey({ value, onChange, onSave, onSkip, validating, error }) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-100">Anthropic API key <span className="ml-2 text-xs text-slate-500">(optional but recommended)</span></h2>
        <p className="mt-1 text-sm text-slate-400">
          Powers the AI features: <strong>CV + LinkedIn PDF parsing on the next steps</strong>, per-job cover letter generation, interview prep, tailored CV summaries, company research. Cost is ~$0.005-$0.025 per generation; you pay Anthropic directly. Without it the scanner + scorer still work — you just won't see the ✨ buttons, and you'll need to fill the profile by hand.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Get a key at <a className="underline decoration-dotted" target="_blank" rel="noreferrer" href="https://console.anthropic.com/settings/keys">console.anthropic.com/settings/keys</a>. We store it AES-256-GCM encrypted on the server.
        </p>
      </div>
      <div>
        <input
          type="password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="sk-ant-…"
          disabled={validating}
          className={
            'w-full rounded-md border bg-slate-950/40 px-3 py-1.5 text-sm font-mono text-slate-100 focus:outline-none disabled:opacity-50 ' +
            (error ? 'border-red-700 focus:border-red-500' : 'border-slate-700 focus:border-brand-500')
          }
          autoComplete="off"
        />
        {error && (
          <p role="alert" className="mt-1 flex items-start gap-1 text-[11px] text-red-400">
            <span aria-hidden="true">⚠</span><span>{error}</span>
          </p>
        )}
        {!error && value.trim() && (
          <p className="mt-1 text-xs text-slate-500">
            Save will ping Anthropic with a 0-cost countTokens call to confirm the key works before storing it.
          </p>
        )}
      </div>
      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={onSkip}
          disabled={validating}
          className="rounded-md border border-slate-700 px-4 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400 disabled:opacity-50"
        >
          Skip for now
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={validating}
          className="rounded-md bg-brand-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-brand-500 disabled:opacity-50"
        >
          {validating ? 'validating with Anthropic…' : value.trim() ? 'Validate + save key →' : 'Continue →'}
        </button>
      </div>
    </div>
  );
}

// ─── Step 6: Done — setup summary + first-scan progress ──────────────────
function StepDone({ summary, linkedinImported, aiKeySet, prefs, scanStatus, scanResult, scanTopRecs, topRecsFailed, alreadyRan, onFinish, finishing }) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-100">Setup complete 🎉</h2>
        <p className="mt-1 text-sm text-slate-400">
          Hunter is running the first scan now so you've got something to look at the moment you land on the dashboard.
        </p>
      </div>

      <ul className="space-y-2 rounded-lg border border-slate-800 bg-slate-950/40 p-4 text-sm">
        <SummaryLine ok={!!summary} text={summary ? `CV parsed (${summary.skills} skills, ${summary.experience} roles)` : 'No CV parsed (you can upload one from Profile later)'} />
        <SummaryLine ok={linkedinImported} text={linkedinImported ? 'LinkedIn data merged' : 'LinkedIn import skipped'} optional />
        <SummaryLine ok={prefs.targetRoles.length > 0 && prefs.targetLocations.length > 0} text={`Hunting: ${prefs.targetRoles.join(', ') || '(no roles)'} in ${prefs.targetLocations.join(', ') || '(no locations)'}`} />
        <SummaryLine ok={aiKeySet} text={aiKeySet ? 'AI features enabled' : 'AI features disabled (skipped key)'} optional />
      </ul>

      {/* T073 — first scan auto-trigger. Render different copy per state. */}
      <FirstScanPanel status={scanStatus} result={scanResult} topRecs={scanTopRecs} topRecsFailed={topRecsFailed} alreadyRan={alreadyRan} />

      <button
        type="button"
        onClick={onFinish}
        disabled={finishing}
        className="rounded-md bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-50"
      >
        {finishing ? 'finishing…' : 'Open dashboard →'}
      </button>
    </div>
  );
}

export function FirstScanPanel({ status, result, topRecs, topRecsFailed, alreadyRan = false }) {
  if (status === 'idle') {
    // `alreadyRan` comes from the resume payload: the scan happened in an
    // earlier visit and its result is not in memory. Without this the Done step
    // rendered nothing at all after a refresh — the guard that stops a second
    // scan from being enqueued also removed the only sign the first one ran.
    if (!alreadyRan) return null;
    return (
      <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4">
        <p className="text-sm font-medium text-slate-200">Your first scan already ran</p>
        <p className="mt-1 text-xs text-slate-400">
          The results are on your dashboard — finish setup to see them.
        </p>
      </div>
    );
  }

  if (status === 'running') {
    return (
      <div className="rounded-lg border border-brand-700 bg-brand-600/10 p-4">
        <div className="flex items-center gap-2 text-brand-300">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-brand-500" />
          <p className="text-sm font-medium">Running first scan against your active sources…</p>
        </div>
        <p className="mt-1 text-xs text-brand-200/80">
          {/* T269(2) — "11 adapters" was written when there were eleven; there
              are now sixty-odd, and a number in copy is a number that goes
              stale silently. */}
          Querying all your active sources, ~5-15s each. Hang tight — this only runs once per setup.
          {result?.sourcesDone > 0 && (
            <span className="text-brand-300"> · {result.sourcesDone} done so far</span>
          )}
        </p>
      </div>
    );
  }

  if (status === 'slow') {
    // The scan is still running server-side. Saying anything about its results
    // here would be a guess, and the previous version's guess was "done".
    return (
      <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4">
        <p className="text-sm font-medium text-slate-200">Your first scan is still running</p>
        <p className="mt-1 text-xs text-slate-400">
          It keeps going in the background. Finish setup and it will be waiting on your dashboard.
        </p>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="rounded-lg border border-amber-700 bg-amber-950/15 p-4">
        <p className="text-sm font-medium text-amber-300">Scan didn't complete</p>
        <p className="mt-1 text-xs text-amber-200">
          {result?.error ?? 'Unknown error.'} You can re-run from the dashboard with the "Scan now" button.
        </p>
      </div>
    );
  }

  // status === 'done'
  const total = result?.totalJobsFound ?? 0;
  const newCount = result?.totalJobsNew ?? 0;
  return (
    <div className="rounded-lg border border-emerald-800 bg-emerald-950/20 p-4">
      <p className="text-sm font-medium text-emerald-300">
        First scan done — {total} match{total === 1 ? '' : 'es'}{newCount > 0 ? ` (${newCount} new)` : ''}
      </p>
      {topRecs?.length > 0 ? (
        <>
          <p className="mt-2 text-[10px] uppercase tracking-wide text-emerald-400/80">Top picks</p>
          <ul className="mt-1 space-y-1.5 text-xs text-emerald-100">
            {topRecs.slice(0, 3).map((r) => (
              <li key={r.id} className="flex items-baseline justify-between gap-2">
                <span className="min-w-0 truncate">
                  <strong className="text-emerald-200">{r.job?.title}</strong>
                  <span className="text-emerald-300/70"> @ {r.job?.company}</span>
                </span>
                <span className="shrink-0 rounded border border-emerald-700/60 px-1.5 py-0.5 font-mono text-[10px] text-emerald-300">
                  {r.scoreTotal}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : topRecsFailed ? (
        <p className="mt-1 text-xs text-emerald-200/90">
          Scan finished — the preview of your top picks could not be loaded, but they are on your dashboard.
        </p>
      ) : total === 0 ? (
        <p className="mt-1 text-xs text-emerald-200/90">
          No matches yet — try widening your <strong>target roles</strong> or <strong>target locations</strong> from the Profile page after you land on the dashboard.
        </p>
      ) : null}
    </div>
  );
}

function SummaryLine({ ok, text, optional }) {
  const tone = ok ? 'text-emerald-300' : optional ? 'text-slate-500' : 'text-amber-400';
  // T134 — the status (done / optional / needs-attention) was color + glyph only;
  // announce it to screen readers instead of relying on the ✓/○/⚠ hue.
  const status = ok ? 'Done' : optional ? 'Optional' : 'Needs attention';
  return (
    <li className={'flex items-start gap-2 ' + tone}>
      <span className="shrink-0" aria-hidden="true">{ok ? '✓' : optional ? '○' : '⚠'}</span>
      <span className="sr-only">{status}: </span>
      <span>{text}</span>
    </li>
  );
}

// ─── Shared helpers ──────────────────────────────────────────────────────
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wide text-slate-400">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

// T266(3) — `className="hidden"` is display:none, which removes the input from
// the accessibility tree AND the tab order, and dropping is a mouse gesture. So
// the single required action of the CV step could not be performed from a
// keyboard at all. sr-only keeps the input focusable; focus-within paints the
// ring on the visible box so the operator can see where he is.
function FileDrop({ accept, disabled, label, onFile }) {
  const [dragging, setDragging] = useState(false);
  const onChange = (e) => {
    const f = e.target.files?.[0];
    if (f) onFile(f);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };
  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      className={
        'flex h-32 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed text-sm transition focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-600 ' +
        (disabled
          ? 'border-slate-800 bg-slate-900/30 text-slate-500'
          : dragging
            ? 'border-brand-500 bg-brand-600/10 text-brand-400'
            : 'border-slate-700 text-slate-400 hover:border-brand-500 hover:text-brand-500')
      }
    >
      <input type="file" accept={accept} disabled={disabled} className="sr-only" onChange={onChange} />
      <span>{dragging ? 'drop to upload' : label}</span>
      <span className="mt-1 text-xs text-slate-600">{accept.replace(/application\/[^,]+,?/g, '').trim() || 'any file'}</span>
    </label>
  );
}
